// app.routes.ts
import { Routes } from '@angular/router';
import { AppRoutes } from '@core/constants/app.routes';
import { UserRole } from '@core/entities/models/role.model';
import { roleGuard } from '@core/guards/role.guard';

import { SimpleLayout } from '@core/layout/simple-layout/simple-layout';
import { MainLayout } from '@core/layout/main-layout/main-layout';
import { NotFound } from '@features/not-found/not-found';

// Import existing components (will be reused)
import { CountriesList } from '@features/countries/page/countries-list/countries-list';
import { PartnersList } from '@features/partners/page/partners-list/partners-list';
import { BeneficiariesList } from '@features/beneficiaries/page/beneficiaries-list/beneficiaries-list';
import { FormsList } from '@features/forms/page/forms-list/forms-list';

// Import upsert components
import { CountryUpsert } from '@features/countries/page/country-upsert/country-upsert';
import { PartnerUpsert } from '@features/partners/page/partner-upsert/partner-upsert';
import { RedirectByRoleComponent } from '@shared/components/organisms/ui-organism-redirect-by-role/ui-organism-redirect-by-role.component';
import { FormUpsert } from '@features/forms/page/form-upsert/form-upsert';
import { BeneficiaryDetail } from '@features/beneficiaries/page/beneficiary-detail/beneficiary-detail';
import { Dashboard } from '@features/dashboard/page/dashboard/dashboard';

/**
 * ROUTES DE LA APLICACIÓN.
 */
export const routes: Routes = [
  // Redirección a login por defecto
  {
    path: AppRoutes.root,
    pathMatch: 'full',
    redirectTo: `${AppRoutes.auth}/${AppRoutes.login}`,
  },

  // Layout simple para auth (login, etc.)
  {
    path: '',
    component: SimpleLayout,
    children: [
      {
        path: AppRoutes.auth,
        loadChildren: () => import('./features/auth/auth.routes').then((m) => m.AUTH_ROUTES),
      },
      // Public form routes (no authentication required)
      {
        path: 'public/form/:formId',
        loadChildren: () => import('./features/public/public.routes').then((m) => m.PUBLIC_ROUTES),
      },
    ],
  },

  // Área principal protegida por roleGuard
  {
    path: '',
    component: MainLayout,
    canActivate: [roleGuard],
    children: [
      // New RESTful admin routes
      {
        path: AppRoutes.admin,
        children: [
          // Dashboard - Todos los roles. El contenido depende del país/partner
          // en scope, que viaja como parámetro de ruta (igual que beneficiaries).
          {
            path: AppRoutes.dashboard,
            component: Dashboard,
            canActivate: [roleGuard],
            data: { roles: [UserRole.ADMIN, UserRole.COUNTRY_ADMIN, UserRole.PARTNER_USER] },
          },
          {
            path: `${AppRoutes.countries}/:countryId/${AppRoutes.dashboard}`,
            component: Dashboard,
            canActivate: [roleGuard],
            data: { roles: [UserRole.ADMIN, UserRole.COUNTRY_ADMIN] },
          },
          {
            path: `${AppRoutes.countries}/:countryId/${AppRoutes.partners}/:partnerId/${AppRoutes.dashboard}`,
            component: Dashboard,
            canActivate: [roleGuard],
            data: { roles: [UserRole.ADMIN, UserRole.COUNTRY_ADMIN, UserRole.PARTNER_USER] },
          },

          // Countries - Solo SUPER_ADMIN
          {
            path: AppRoutes.countries,
            canActivate: [roleGuard],
            data: { roles: [UserRole.ADMIN] },
            children: [
              { path: '', component: CountriesList },
              { path: 'create', component: CountryUpsert },
              { path: ':id/edit', component: CountryUpsert },
            ],
          },

          // Partners of a Country - SUPER_ADMIN y COUNTRY_ADMIN
          {
            path: `${AppRoutes.countries}/:countryId/${AppRoutes.partners}`,
            canActivate: [roleGuard],
            data: { roles: [UserRole.ADMIN, UserRole.COUNTRY_ADMIN] },
            children: [
              { path: '', component: PartnersList },
              { path: 'create', component: PartnerUpsert },
              { path: ':id/edit', component: PartnerUpsert },
            ],
          },

          // Beneficiaries of a Partner - Todos los roles
          {
            path: `${AppRoutes.countries}/:countryId/${AppRoutes.partners}/:partnerId/${AppRoutes.beneficiaries}`,
            canActivate: [roleGuard],
            data: { roles: [UserRole.ADMIN, UserRole.COUNTRY_ADMIN, UserRole.PARTNER_USER] },
            children: [
              { path: '', component: BeneficiariesList },
              { path: ':id', component: BeneficiaryDetail },
            ],
          },

          // Legacy route for direct partner access - redirige al home según rol
          {
            path: `${AppRoutes.partners}/:partnerId/${AppRoutes.beneficiaries}`,
            component: RedirectByRoleComponent,
          },

          // Global Users - Solo SUPER_ADMIN
          {
            path: AppRoutes.users,
            redirectTo: AppRoutes.countries,
          },

          // Global Forms - Solo SUPER_ADMIN
          {
            path: AppRoutes.forms,
            canActivate: [roleGuard],
            data: { roles: [UserRole.ADMIN] },
            children: [
              { path: '', component: FormsList },
              { path: 'create', component: FormUpsert },
              { path: ':id/edit', component: FormUpsert },
            ],
          },
        ],
      },

      // Legacy routes (to be removed after migration)
      {
        path: AppRoutes.beneficiaries,
        canActivate: [roleGuard],
        data: { roles: [UserRole.ADMIN, UserRole.COUNTRY_ADMIN, UserRole.PARTNER_USER] },
        children: [{ path: '', component: BeneficiariesList }],
      },

      {
        path: AppRoutes.forms,
        canActivate: [roleGuard],
        data: { roles: [UserRole.ADMIN, UserRole.COUNTRY_ADMIN, UserRole.PARTNER_USER] },
        children: [
          { path: '', component: FormsList },
          {
            path: AppRoutes.formsCreate,
            component: FormUpsert,
            canActivate: [roleGuard],
            data: { roles: [UserRole.ADMIN] },
          },
          {
            path: `${AppRoutes.formsEdit}/:id`,
            component: FormUpsert,
            canActivate: [roleGuard],
            data: { roles: [UserRole.ADMIN] },
          },
        ],
      },

      {
        path: AppRoutes.partners,
        canActivate: [roleGuard],
        data: { roles: [UserRole.ADMIN, UserRole.COUNTRY_ADMIN] },
        children: [
          { path: '', component: PartnersList },
          {
            path: AppRoutes.partnersCreate,
            component: PartnerUpsert,
            canActivate: [roleGuard],
            data: { roles: [UserRole.ADMIN] },
          },
          {
            path: `${AppRoutes.partnersEdit}/:id`,
            component: PartnerUpsert,
            canActivate: [roleGuard],
            data: { roles: [UserRole.ADMIN] },
          },
        ],
      },

      {
        path: AppRoutes.countries,
        canActivate: [roleGuard],
        data: { roles: [UserRole.ADMIN] },
        children: [
          { path: '', component: CountriesList },
          { path: AppRoutes.countriesCreate, component: CountryUpsert },
          { path: `${AppRoutes.countriesEdit}/:id`, component: CountryUpsert },
        ],
      },
    ],
  },

  // 404 dedicada
  {
    path: AppRoutes.notFound,
    component: NotFound,
  },

  // Cualquier ruta desconocida → 404
  {
    path: '**',
    redirectTo: AppRoutes.notFound,
  },
];
